import * as React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

export interface ActionMenuItem {
  /** Stable key for React lists. */
  key: string;
  label: React.ReactNode;
  /** Leading icon element (e.g. <Trash2 className="w-5 h-5" />) — already styled. */
  icon?: React.ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  destructive?: boolean;
  /** Insert a divider above this row. */
  separatorBefore?: boolean;
}

interface ActionMenuProps {
  /** Trigger element — receives Radix asChild semantics. Must accept a ref / forward props. */
  trigger: React.ReactNode;
  items: ActionMenuItem[];
  /** Desktop-only: dropdown alignment. */
  align?: 'start' | 'end' | 'center';
  /** Optional title shown at top of mobile sheet. */
  mobileTitle?: string;
}

/**
 * Unified action menu: bottom sheet on mobile, dropdown on desktop.
 * Items[] is the single source of truth so the two branches can't drift.
 *
 * Use for ACTION menus (verbs that mutate state: Edit, Duplicate, Delete).
 * For SELECTION menus (sort/filter pickers), keep DropdownMenu directly.
 */
export function ActionMenu({ trigger, items, align = 'end', mobileTitle }: ActionMenuProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side="bottom"
          className="bg-chrome border-t border-white/[0.06] rounded-t-2xl p-0 pb-[max(env(safe-area-inset-bottom),1rem)] max-h-[85dvh]"
        >
          {mobileTitle && (
            <div className="px-5 pt-1 pb-2 text-[13px] uppercase tracking-wide text-muted-foreground/80">
              {mobileTitle}
            </div>
          )}
          <div className="px-2 pb-3 pt-1">
            {items.map((item, idx) => (
              <React.Fragment key={item.key}>
                {item.separatorBefore && idx > 0 && (
                  <div className="h-px bg-white/[0.06] my-1.5 mx-3" />
                )}
                <button
                  type="button"
                  disabled={item.disabled}
                  onClick={() => {
                    if (item.disabled) return;
                    setOpen(false);
                    // Defer to next tick so the sheet close animation can start cleanly
                    setTimeout(() => item.onSelect(), 0);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-[16px] font-medium transition-colors',
                    'hover:bg-white/[0.04] active:bg-white/[0.06]',
                    item.disabled && 'opacity-40 pointer-events-none',
                    item.destructive ? 'text-destructive' : 'text-foreground'
                  )}
                >
                  {item.icon && (
                    <span className={cn('shrink-0 flex items-center justify-center', item.destructive ? 'text-destructive' : 'text-muted-foreground')}>
                      {item.icon}
                    </span>
                  )}
                  <span className="flex-1 min-w-0">{item.label}</span>
                </button>
              </React.Fragment>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-64 p-1.5">
        {items.map((item, idx) => (
          <React.Fragment key={item.key}>
            {item.separatorBefore && idx > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={item.onSelect}
              disabled={item.disabled}
              className={cn(
                'py-3 px-3 text-sm cursor-pointer',
                item.destructive && 'text-white focus:text-white'
              )}
            >
              {item.icon && (
                <span className={cn('mr-3 flex items-center', item.destructive ? 'text-destructive' : 'text-muted-foreground')}>
                  {item.icon}
                </span>
              )}
              {item.label}
            </DropdownMenuItem>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
